import { useState } from "react";
import { ArrowLeft, Building2, Check, ChevronRight, KeyRound, MailCheck, Sparkles, UserRound } from "lucide-react";
import { api } from "@/lib/api";

const BUSINESS_TYPES = ["Perdagangan / Ritel", "Jasa", "Manufaktur / Produksi", "Makanan & Minuman", "Fashion & Tekstil", "Konstruksi", "Teknologi", "Agrikultur", "Lainnya"];
const weakPassword = (pw) => pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82Z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24Z" />
    <path fill="#FBBC05" d="M5.27 14.29A7.16 7.16 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09Z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.94 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z" />
  </svg>
);

export default function Login({ onLogin, initialMode = "login", appName = "Kiyana IAC Solution", onBack }) {
  const resetToken = new URLSearchParams(window.location.search).get("reset_token") || "";
  const [mode, setMode] = useState(resetToken ? "reset" : initialMode);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPass, setNewPass] = useState({ password: "", confirm: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "", confirm: "", company_name: "", business_type: "", phone: "", city: "", address: "", npwp: "" });
  const [error, setError] = useState(() => {
    const n = sessionStorage.getItem("auth_notice");
    if (n) sessionStorage.removeItem("auth_notice");
    return n || "";
  });
  const [notice, setNotice] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [saving, setSaving] = useState(false);
  const [regOpen, setRegOpen] = useState(true);
  const set = (k) => (e) => setReg({ ...reg, [k]: e.target.value });
  const needsVerify = error.includes("belum diverifikasi");
  useState(() => { if (initialMode === "register") api("/auth/registration-status").then((s) => setRegOpen(s.allow_registration)).catch(() => {}); });
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice("");
    try {
      const r = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      if (r.otp_required) { setOtp(""); setNotice(r.message); setMode("otp"); return; }
      onLogin(r);
    }
    catch (err) { setError(err.message === "Failed to fetch" ? "Server sedang dimulai atau koneksi terputus — tunggu beberapa detik lalu coba lagi." : err.message); }
  };
  const otpSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try { onLogin(await api("/auth/verify-otp", { method: "POST", body: JSON.stringify({ email, code: otp.trim() }) })); }
    catch (err) { setError(err.message); }
    setSaving(false);
  };
  const otpResend = async () => {
    setSaving(true); setError("");
    try {
      const r = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      if (r.otp_required) setNotice("Kode baru telah dikirim — periksa email Anda.");
      else onLogin(r);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };
  const resend = async () => {
    setSaving(true);
    try { const r = await api("/auth/resend-verification", { method: "POST", body: JSON.stringify({ email }) }); setError(""); setNotice(r.message); }
    catch (err) { setError(err.message); }
    setSaving(false);
  };
  const forgotSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try { const r = await api("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }); setNotice(r.message); }
    catch (err) { setError(err.message); }
    setSaving(false);
  };
  const resetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (weakPassword(newPass.password)) return setError("Kata sandi minimal 8 karakter dan harus mengandung kombinasi huruf serta angka");
    if (newPass.password !== newPass.confirm) return setError("Konfirmasi kata sandi tidak sama");
    setSaving(true);
    try {
      const r = await api("/auth/reset-password", { method: "POST", body: JSON.stringify({ token: resetToken, password: newPass.password }) });
      window.history.replaceState({}, "", "/");
      setMode("login"); setNotice(r.message);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };
  const nextStep = (e) => {
    e.preventDefault();
    setError("");
    if (weakPassword(reg.password)) return setError("Kata sandi minimal 8 karakter dan harus mengandung kombinasi huruf serta angka");
    if (reg.password !== reg.confirm) return setError("Konfirmasi kata sandi tidak sama");
    setStep(2);
  };
  const registerSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const { confirm, ...payload } = reg;
      const r = await api("/auth/register", { method: "POST", body: JSON.stringify(payload) });
      if (r.verification_required) { setVerifyEmail(r.email); setMode("verify-sent"); }
      else onLogin(r);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };
  const switchMode = (m) => {
    setMode(m); setStep(1); setError(""); setNotice("");
    if (m === "register") api("/auth/registration-status").then((s) => setRegOpen(s.allow_registration)).catch(() => {});
  };
  return (
    <main className="auth-page">
      <section className="auth-panel">
        {onBack && <button type="button" data-testid="auth-back-landing" className="auth-back" onClick={onBack}><ArrowLeft size={13} /> Kembali ke beranda</button>}
        <div className="brand-mark">{appName.trim().charAt(0).toUpperCase()}<span>•</span></div>
        <p className="eyebrow">{appName.toUpperCase()}</p>
        <h1>Keuangan yang terasa lebih terkendali.</h1>
        <p className="auth-copy">Kelola penjualan, persediaan, produksi, dan laporan keuangan bisnis Anda dalam satu ruang kerja.</p>
        <div className="auth-note"><Sparkles size={16} /> Satu akun untuk satu perusahaan — data terisolasi & aman</div>
      </section>
      <section className="auth-form-wrap">
        {mode === "login" && (
          <form className="auth-form" onSubmit={submit}>
            <p className="eyebrow">Selamat datang kembali</p>
            <h2>Masuk ke workspace</h2>
            <p className="muted">Gunakan akun Anda untuk melanjutkan.</p>
            <label>Email<input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="nama@perusahaan.co.id" required /></label>
            <label>Kata sandi<input data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Kata sandi Anda" required /></label>
            {notice && <div data-testid="login-notice" className="form-notice">{notice}</div>}
            {error && <div data-testid="login-error" className="form-error">{error}</div>}
            {needsVerify && <button type="button" data-testid="resend-verification-button" className="secondary-btn auth-resend" disabled={saving} onClick={resend}>{saving ? "Mengirim..." : "Kirim ulang email verifikasi"}</button>}
            <button data-testid="login-submit-button" className="primary-btn" type="submit">Masuk <ChevronRight size={17} /></button>
            <div className="auth-divider"><span>atau</span></div>
            <button type="button" data-testid="google-login-button" className="google-btn" onClick={googleLogin}><GoogleIcon /> Masuk dengan Google</button>
            <p className="form-foot"><button type="button" data-testid="goto-forgot" className="link-btn" onClick={() => switchMode("forgot")}>Lupa kata sandi?</button></p>
            <p className="form-foot">Belum punya akun? <button type="button" data-testid="goto-register" className="link-btn" onClick={() => switchMode("register")}>Daftar & buat perusahaan</button></p>
          </form>
        )}
        {mode === "otp" && (
          <form className="auth-form" onSubmit={otpSubmit} data-testid="otp-form">
            <span className="auth-mode-ic"><MailCheck size={20} /></span>
            <h2>Verifikasi perangkat baru</h2>
            <p className="muted">Masukkan kode 6 digit yang kami kirim ke <b>{email}</b>. Kode berlaku 10 menit.</p>
            <label>Kode OTP<input data-testid="otp-code-input" className="otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="••••••" autoFocus required /></label>
            {notice && <div data-testid="otp-notice" className="form-notice">{notice}</div>}
            {error && <div data-testid="otp-error" className="form-error">{error}</div>}
            <button data-testid="otp-submit-button" className="primary-btn" type="submit" disabled={saving || otp.length !== 6}>{saving ? "Memverifikasi..." : "Verifikasi & Masuk"} <ChevronRight size={17} /></button>
            <p className="form-foot">Tidak menerima kode? <button type="button" data-testid="otp-resend-button" className="link-btn" disabled={saving} onClick={otpResend}>Kirim ulang</button></p>
            <p className="form-foot"><button type="button" className="link-btn" onClick={() => switchMode("login")}><ArrowLeft size={12} /> Kembali ke halaman masuk</button></p>
          </form>
        )}
        {mode === "forgot" && (
          <form className="auth-form" onSubmit={forgotSubmit} data-testid="forgot-form">
            <span className="auth-mode-ic"><KeyRound size={20} /></span>
            <h2>Lupa kata sandi</h2>
            <p className="muted">Masukkan email akun Anda — kami akan mengirim tautan untuk mengatur ulang kata sandi (berlaku 1 jam).</p>
            <label>Email<input data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="nama@perusahaan.co.id" required /></label>
            {notice && <div data-testid="forgot-notice" className="form-notice">{notice}</div>}
            {error && <div data-testid="forgot-error" className="form-error">{error}</div>}
            <button data-testid="forgot-submit-button" className="primary-btn" type="submit" disabled={saving}>{saving ? "Mengirim..." : "Kirim Tautan Reset"} <ChevronRight size={17} /></button>
            <p className="form-foot"><button type="button" data-testid="back-to-login" className="link-btn" onClick={() => switchMode("login")}><ArrowLeft size={12} /> Kembali ke halaman masuk</button></p>
          </form>
        )}
        {mode === "reset" && (
          <form className="auth-form" onSubmit={resetSubmit} data-testid="reset-form">
            <span className="auth-mode-ic"><KeyRound size={20} /></span>
            <h2>Atur ulang kata sandi</h2>
            <p className="muted">Buat kata sandi baru untuk akun Anda — minimal 8 karakter dengan kombinasi huruf dan angka.</p>
            <label>Kata sandi baru<input data-testid="reset-password-input" value={newPass.password} onChange={(e) => setNewPass({ ...newPass, password: e.target.value })} type="password" placeholder="Min. 8 karakter, huruf & angka" required /></label>
            <label>Ulangi kata sandi<input data-testid="reset-confirm-input" value={newPass.confirm} onChange={(e) => setNewPass({ ...newPass, confirm: e.target.value })} type="password" placeholder="Ketik ulang" required /></label>
            {error && <div data-testid="reset-error" className="form-error">{error}</div>}
            <button data-testid="reset-submit-button" className="primary-btn" type="submit" disabled={saving}>{saving ? "Menyimpan..." : "Simpan Kata Sandi Baru"} <ChevronRight size={17} /></button>
            <p className="form-foot"><button type="button" data-testid="back-to-login" className="link-btn" onClick={() => { window.history.replaceState({}, "", "/"); switchMode("login"); }}><ArrowLeft size={12} /> Kembali ke halaman masuk</button></p>
          </form>
        )}
        {mode === "verify-sent" && (
          <div className="auth-form" data-testid="verify-sent">
            <span className="auth-mode-ic ok"><MailCheck size={20} /></span>
            <h2>Periksa email Anda</h2>
            <p className="muted">Kami telah mengirim tautan verifikasi ke <b>{verifyEmail}</b>. Klik tautan tersebut untuk mengaktifkan akun, lalu masuk. Jangan lupa cek folder spam.</p>
            <button type="button" data-testid="verify-back-login" className="primary-btn" onClick={() => { setEmail(verifyEmail); switchMode("login"); }}>Ke Halaman Masuk <ChevronRight size={17} /></button>
          </div>
        )}
        {mode === "register" && (!regOpen ? (
          <div className="auth-form register" data-testid="registration-closed">
            <h2>Pendaftaran sedang ditutup</h2>
            <p className="muted">Untuk saat ini pendaftaran perusahaan baru belum dibuka. Silakan hubungi tim kami untuk mendapatkan akses.</p>
            <p className="form-foot">Sudah punya akun? <button type="button" data-testid="goto-login" className="link-btn" onClick={() => switchMode("login")}>Masuk di sini</button></p>
          </div>
        ) : (
          <form className="auth-form register" onSubmit={step === 1 ? nextStep : registerSubmit}>
            <div className="reg-steps">
              <span className={`reg-step ${step >= 1 ? "on" : ""}`}><i>{step > 1 ? <Check size={11} strokeWidth={3.5} /> : <UserRound size={12} />}</i> Data Akun</span>
              <span className="reg-step-line" />
              <span className={`reg-step ${step === 2 ? "on" : ""}`}><i><Building2 size={12} /></i> Data Perusahaan</span>
            </div>
            {step === 1 ? (
              <>
                <h2>Buat akun Anda</h2>
                <p className="muted">Akun ini akan menjadi Pemilik (Owner) perusahaan.</p>
                <label>Nama lengkap<input data-testid="reg-name-input" value={reg.name} onChange={set("name")} placeholder="mis. Rahmat Dermawan" required /></label>
                <label>Email<input data-testid="reg-email-input" value={reg.email} onChange={set("email")} type="email" placeholder="nama@perusahaan.co.id" required /></label>
                <div className="reg-grid">
                  <label>Kata sandi<input data-testid="reg-password-input" value={reg.password} onChange={set("password")} type="password" placeholder="Min. 8 karakter, huruf & angka" required /></label>
                  <label>Ulangi kata sandi<input data-testid="reg-confirm-input" value={reg.confirm} onChange={set("confirm")} type="password" placeholder="Ketik ulang" required /></label>
                </div>
                {error && <div data-testid="register-error" className="form-error">{error}</div>}
                <button data-testid="reg-next-button" className="primary-btn" type="submit">Lanjut: Data Perusahaan <ChevronRight size={17} /></button>
                <div className="auth-divider"><span>atau</span></div>
                <button type="button" data-testid="google-register-button" className="google-btn" onClick={googleLogin}><GoogleIcon /> Daftar dengan Google</button>
              </>
            ) : (
              <>
                <h2>Tentang perusahaan Anda</h2>
                <p className="muted">Satu akun hanya untuk satu perusahaan. Data ini tampil di invoice & laporan.</p>
                <label>Nama perusahaan<input data-testid="reg-company-input" value={reg.company_name} onChange={set("company_name")} placeholder="mis. PT Maju Bersama" required /></label>
                <div className="reg-grid">
                  <label>Jenis usaha
                    <select data-testid="reg-business-select" value={reg.business_type} onChange={set("business_type")} required>
                      <option value="" disabled>Pilih jenis usaha</option>
                      {BUSINESS_TYPES.map((b) => <option key={b}>{b}</option>)}
                    </select>
                  </label>
                  <label>No. telepon<input data-testid="reg-phone-input" value={reg.phone} onChange={set("phone")} placeholder="mis. 021-555-0123" /></label>
                </div>
                <div className="reg-grid">
                  <label>Kota<input data-testid="reg-city-input" value={reg.city} onChange={set("city")} placeholder="mis. Jakarta Selatan" /></label>
                  <label>NPWP <em className="opt">(opsional)</em><input data-testid="reg-npwp-input" value={reg.npwp} onChange={set("npwp")} placeholder="00.000.000.0-000.000" /></label>
                </div>
                <label>Alamat perusahaan<input data-testid="reg-address-input" value={reg.address} onChange={set("address")} placeholder="Jalan, nomor, kelurahan, kecamatan" /></label>
                {error && <div data-testid="register-error" className="form-error">{error}</div>}
                <button data-testid="reg-submit-button" className="primary-btn" type="submit" disabled={saving}>{saving ? "Menyiapkan perusahaan..." : "Daftar & Mulai Sekarang"} <ChevronRight size={17} /></button>
                <button type="button" data-testid="reg-back-button" className="link-btn back" onClick={() => { setStep(1); setError(""); }}><ArrowLeft size={13} /> Kembali ke data akun</button>
              </>
            )}
            <p className="form-foot">Sudah punya akun? <button type="button" data-testid="goto-login" className="link-btn" onClick={() => switchMode("login")}>Masuk di sini</button></p>
          </form>
        ))}
      </section>
    </main>
  );
}
